import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  TrendingUp,
  ArrowUpRight,
  Plus,
  Eye,
  Zap,
  Shield,
  Activity
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated Counter Component
function AnimatedCounter({ value, duration = 1.5 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    const incrementTime = (duration * 1000) / end;
    
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
}

function MetricCard({ title, value, change, icon: Icon, gradient, delay, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      data-testid={testId}
    >
      <div className="glass-card glass-card-hover stat-card rounded-2xl p-6 h-full">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {change && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium">
              <TrendingUp className="h-3 w-3" />
              {change}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{title}</p>
          <p className="text-4xl font-bold text-white font-mono-data">
            <AnimatedCounter value={value} />
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function RiskBadge({ band }) {
  const config = {
    RED: { 
      gradient: "from-red-500/20 to-red-600/10",
      dot: "risk-dot-red",
      text: "text-red-400",
      label: "HIGH RISK"
    },
    AMBER: { 
      gradient: "from-amber-500/20 to-amber-600/10",
      dot: "risk-dot-amber",
      text: "text-amber-400",
      label: "MEDIUM"
    },
    GREEN: { 
      gradient: "from-emerald-500/20 to-emerald-600/10",
      dot: "risk-dot-green",
      text: "text-emerald-400",
      label: "LOW RISK"
    }
  };
  const { gradient, dot, text, label } = config[band] || config.GREEN;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${gradient}`}>
      <div className={`risk-dot ${dot}`} />
      <span className={`text-xs font-semibold tracking-wide ${text}`}>{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalClients: 0, highRisk: 0, pending: 0, approved: 0, newThisWeek: 0 });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/activity`)
      ]);
      setStats(statsRes.data);
      setActivities(activityRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden glass-card rounded-3xl p-8"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-transparent" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2 mb-3"
            >
              <Zap className="h-4 w-4 text-amber-400" />
              <span className="text-xs text-amber-400 font-medium uppercase tracking-wider">Quick Actions</span>
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome to <span className="gradient-text">DueDiligence AI</span>
            </h1>
            <p className="text-slate-400 max-w-md">
              AI-powered compliance platform for intelligent client risk assessment and monitoring.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                onClick={() => navigate('/onboard')}
                className="btn-premium text-white rounded-xl px-6 py-6 text-sm font-semibold shadow-lg"
                data-testid="new-client-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Client Assessment
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline" 
                onClick={() => navigate('/cases')}
                className="border-white/10 hover:bg-white/5 hover:border-white/20 rounded-xl px-6 py-6 text-sm font-semibold"
                data-testid="view-cases-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Open Cases
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard 
          title="Total Clients" 
          value={stats.totalClients} 
          change={`+${stats.newThisWeek}`}
          icon={Users} 
          gradient="from-blue-500 to-blue-600"
          delay={0}
          testId="metric-total-clients"
        />
        <MetricCard 
          title="High Risk" 
          value={stats.highRisk} 
          icon={AlertTriangle} 
          gradient="from-red-500 to-red-600"
          delay={0.1}
          testId="metric-high-risk"
        />
        <MetricCard 
          title="Pending Review" 
          value={stats.pending} 
          icon={Clock} 
          gradient="from-amber-500 to-amber-600"
          delay={0.2}
          testId="metric-pending"
        />
        <MetricCard 
          title="Approved" 
          value={stats.approved} 
          icon={CheckCircle} 
          gradient="from-emerald-500 to-emerald-600"
          delay={0.3}
          testId="metric-approved"
        />
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="glass-card rounded-2xl border-0 overflow-hidden" data-testid="recent-activity">
          <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20">
                <Activity className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
                <p className="text-xs text-slate-500">Latest client assessments and updates</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
              View All
            </Button>
          </div>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="p-6 text-center text-slate-500">
                  <div className="inline-flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Loading activity...
                  </div>
                </div>
              ) : activities.length === 0 ? (
                <div className="p-6 text-center text-slate-500">No recent activity</div>
              ) : (
                <div>
                  {activities.map((activity, index) => (
                    <motion.div 
                      key={activity.activityId || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="table-row-premium flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors cursor-pointer group"
                      onClick={() => navigate('/cases')}
                      data-testid={`activity-item-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <RiskBadge band={activity.riskBand} />
                        <div>
                          <p className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
                            {activity.clientName}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {activity.details || activity.event}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-slate-500 font-mono-data">
                          {formatTime(activity.timestamp)}
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-slate-600 group-hover:text-blue-400 transition-colors" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
